<script lang="ts" setup>
import StorageService from '../../runtime/services/StorageService';
// import VisionService from '@/modules/ai/common/services/VisionService';

interface ErrorItem
{
    file: File;
    error: any;
}

const { $t } = useLocalization();
const { $auth } = useAuth();
const { $alert } = useAlerts();

const props = withDefaults(defineProps<{
    scope?: string;
    folder?: number;
    modelValue: File[];
}>(), {
    scope: 'public',
    folder: null
});

const emit = defineEmits<{
    (e: 'open'): void;
    (e: 'update:modelValue', value: File[]): void;
    (e: 'start'): void;
    (e: 'reload'): void;
    (e: 'send'): void;
    (e: 'cancel'): void;
}>();

const files = ref(null);

const current = ref<File>(null);
const uploading = ref(false);
const totalProgress = ref(0);
const uploadProgress = ref(0);
const success = ref<File[]>([]);
const failures = ref<ErrorItem[]>([]);
const imageMaxSize = ref(2560);
const forceConvert = ref<boolean>(true);
const forceResize = ref<boolean>(true);
const uploadPluginRef = ref(null);

const filesCount = computed(() => props.modelValue.length);
const totalBytes = computed(() => props.modelValue.reduce((sum, file) => sum + file.size, 1));

watch(() => props.modelValue, () =>
{
    totalProgress.value = 0;
});

const isSending = (file: File): boolean =>
{
    return current.value === file;
};

const isSent = (file: File): boolean =>
{
    return success.value.filter(p => p === file).length > 0;
};

const isError = (file: File): boolean =>
{
    return failures.value.filter(p => p.file === file).length > 0;
};

const isWaiting = (file: File): boolean =>
{
    return !isSending(file) && !isSent(file) && !isError(file);
};

const errorMessage = (file: File): string =>
{
    const failure = failures.value.find(p => p.file === file);

    if (failure && failure.error && failure.error.message)
        return failure.error.message;

    return $t('[[[Nie udało się wysłać pliku]]]');
};

const openDialog = (): void =>
{
    files.value.click();
    emit('open');
};

const triggerStart = (): void =>
{
    emit('start');
};

const triggerReload = (): void =>
{
    emit('reload');
};

const triggerSend = (): void =>
{
    emit('send');
};

const triggerCancel = (): void =>
{
    emit('cancel');
};

const triggerInput = (value: File[]): void =>
{
    triggerStart();

    emit('update:modelValue', value);
};

const remove = (file: File): void =>
{
    triggerInput(props.modelValue.filter(p => p !== file));
};

const onChange = (event: Event): void =>
{
    const result: File[] = [];
    const eventFiles = (event.target as HTMLInputElement).files;

    props.modelValue.forEach(p => result.push(p));

    for (let i = 0; i < eventFiles.length; i++)
    {
        result.push(eventFiles.item(i));
    }

    triggerInput(result);
};

const sendRequest = (file: File): Promise<any> =>
{
    current.value = file;
    uploadProgress.value = 0;

    return new Promise((resolve, reject) =>
    {
        const formData = new FormData();
        const req = new XMLHttpRequest();

        formData.append('file', file, file.name);
        formData.append('container', 'default');
        formData.append('scope', props.scope);
        formData.append('forceConvert', forceConvert.value ? 'true' : 'false');
        formData.append('forceResize', forceResize.value ? 'true' : 'false');

        if (props.folder)
        {
            formData.append('folder', props.folder.toString());
        }

        req.upload.addEventListener('progress', (event) =>
        {
            if (event.lengthComputable)
            {
                uploadProgress.value = event.loaded;
            }
        });

        req.upload.addEventListener('load', () =>
        {
            uploadProgress.value = 0;
            totalProgress.value += file.size;
        });

        req.upload.addEventListener('error', () =>
        {
            uploadProgress.value = 0;
            totalProgress.value += file.size;
        });

        req.addEventListener('load', () =>
        {
            if (req.status < 400)
                resolve(JSON.parse(req.response));
            else if (req.response)
                reject(JSON.parse(req.response));
            else
                reject(new Error($t('[[[Podczas przesyłania plików wystąpił nieznany błąd]]]')));
        });

        req.addEventListener('error', () =>
        {
            if (req.response)
                reject(JSON.parse(req.response));
            else
                reject(new Error($t('[[[Podczas przesyłania plików wystąpił nieznany błąd]]]')));
        });

        req.open('POST', StorageService.uploadUrl(), true);
        req.setRequestHeader('Authorization', 'Bearer ' + $auth.token());
        req.setRequestHeader('Request-Token', requestToken(new URL(StorageService.uploadUrl()).pathname, $auth.token()));
        req.send(formData);
    });
};

const uploadFiles = async (): Promise<void> =>
{
    uploading.value = true;
    totalProgress.value = 0;
    success.value = [];
    failures.value = [];

    for (let i = 0; i < props.modelValue.length; i++)
    {
        if (uploading.value)
        {
            const file = props.modelValue[i];

            try
            {
                const resourceFile = await sendRequest(file);

                await uploadPluginRef.value?.postProcess(resourceFile);

                success.value.push(file);
            }
            catch (ex: any)
            {
                failures.value.push({
                    file: file,
                    error: ex
                });
            }
        }
    }

    if (uploading.value)
    {
        nextTick(() =>
        {
            triggerInput(failures.value.map(p => p.file));
            triggerReload();
        });
    }

    if (props.modelValue.length === 0)
    {
        totalProgress.value = 0;
    }

    current.value = null;
    uploading.value = false;

    if (failures.value.length === 0)
    {
        triggerSend();
    }
    else
    {
        $alert.danger(failures.value[0]?.error?.message || $t('[[[Podczas przesyłania plików wystąpił nieznany błąd]]]'));
    }
};

const cancel = async (): Promise<void> =>
{
    if (uploading.value)
    {
        uploading.value = false;
    }
    else
    {
        success.value = [];
        failures.value = [];

        triggerInput([]);
        triggerCancel();
    }
};

defineExpose({
    openDialog
});
</script>

<template>
    <div>
        <input ref="files" type="file" class="file-input" multiple @change="onChange">
        <div v-if="filesCount > 0">
            <div class="row mb-3">
                <div class="col col-6">
                    <button class="btn btn-primary w-100" :disabled="uploading" @click.prevent="uploadFiles()">
                        <i class="fa fa-cloud-upload-alt" />
                        {{ $t('[[[Wyślij]]]') }}
                    </button>
                </div>
                <div class="col col-6">
                    <button class="btn btn-danger w-100" :disabled="uploading" @click.prevent="cancel()">
                        <i class="fa fa-ban" />
                        {{ $t('[[[Anuluj]]]') }}
                    </button>
                </div>
            </div>
            <div class="progress">
                <div class="progress-bar progress-bar-striped bg-primary" :style="{ width: `${(totalProgress / totalBytes) * 100}%` }" />
                <div class="progress-bar progress-bar-striped bg-success" :style="{ width: `${(uploadProgress / totalBytes) * 100}%` }" />
            </div>
            <h6 class="border-bottom py-2">
                {{ $t('[[[Pliki do wysłania: %0|||{0}]]]', filesCount) }}
            </h6>
            <Responsive>
                <ul class="list-unstyled mb-0">
                    <li v-for="(file, index) in modelValue" :key="`${index}.${file.name}`" class="d-flex p-1" :class="{ 'text-danger': isError(file), 'bg-white': index % 2 === 1 }">
                        <span class="me-1">
                            <i v-if="isWaiting(file)" class="far fa-fw fa-clock text-muted" :title="$t('[[[Oczekuje na wysłanie]]]')" />
                            <i v-if="isSending(file)" class="fas fa-fw fa-clock text-primary" :title="$t('[[[Plik jest wysyłany]]]')" />
                            <i v-if="isSent(file)" class="fas fa-fw fa-check text-success" :title="$t('[[[Plik został wysłany]]]')" />
                            <i v-if="isError(file)" class="fas fa-fw fa-exclamation-triangle text-danger" :title="errorMessage(file)" />
                        </span>
                        <span v-if="uploading" class="me-1">
                            <i class="fas fa-fw fa-times text-muted pointer" :title="$t('[[[Usuń plik z listy]]]')" />
                        </span>
                        <span v-else class="me-1">
                            <i class="fas fa-fw fa-times text-danger pointer" :title="$t('[[[Usuń plik z listy]]]')" @click="remove(file)" />
                        </span>
                        <span class="me-2 text-wrap text-break">{{ file.name }}</span>
                        <span class="ms-auto text-nowrap">[{{ $filters.filesize(file.size) }}]</span>
                    </li>
                </ul>
            </Responsive>
            <div class="alert alert-light mt-3 p-2 pb-1">
                <div class="mb-2">
                    <IdeoFormRadio v-model="forceConvert" name="convert" :value="true">
                        {{ $t('[[[Konwertuj obrazy do formatu WebP]]]') }}
                    </IdeoFormRadio>
                    <IdeoFormRadio v-model="forceConvert" name="convert" :value="false">
                        {{ $t('[[[Zachowaj oryginalny format obrazów]]]') }}
                    </IdeoFormRadio>
                </div>
                <IdeoFormCheckbox v-model="forceResize">
                    {{ $t('[[[Optymalizuj rozdzielczość obrazów]]]') }}
                </IdeoFormCheckbox>
                <div class="ps-4 ms-1 mb-2 text-muted small">
                    {{ $t('[[[Maksymalna rozdzielość obrazu po optymalizacji: %0x%1px|||{0}|||{1}]]]', imageMaxSize, imageMaxSize) }}
                </div>
                <FilemanagerUpload ref="uploadPluginRef" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.file-input {
    display: none;
}

.progress {
    background-color: var(--bs-body-bg);
    border: 1px solid var(--bs-border-color);
}
</style>
